import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatPct, formatTry } from '../../utils/finance/financeFormat';
import { expenseAmount, periodExpenseAmount } from '../../utils/finance/financePnl';
import { fx } from './financeTheme';

const PAGE_SIZE = 11;

export default function FinanceDetailDrawer({ open, onClose, title, rows, empty, kind, range }) {
  const [page, setPage] = useState(0);
  const list = rows || [];
  const pages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  const slice = useMemo(
    () => list.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [list, safePage]
  );

  useEffect(() => {
    if (open) setPage(0);
  }, [open, kind, list.length]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 440 },
          maxWidth: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 1.5,
          boxSizing: 'border-box'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 16, flex: 1 }}>{title}</Typography>
        <IconButton onClick={onClose} size="small" aria-label="kapat"><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {!list.length ? (
          <Typography sx={{ fontSize: 13, color: fx.faint }}>{empty}</Typography>
        ) : slice.map((row) => {
          if (kind === 'gider') {
            return (
              <Box key={row.id} sx={{ py: 0.55, borderBottom: `1px solid ${fx.border}`, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{row.title || row.aciklama || row.category}</Typography>
                <Typography sx={{ fontSize: 12, color: fx.muted }}>Aylık {formatTry(expenseAmount(row))} · Bu dönem {formatTry(periodExpenseAmount(row, range))}</Typography>
              </Box>
            );
          }
          const ts = row.date ? row.date.toLocaleString('tr-TR') : '—';
          const tur = row.islem.islemTuru || 'Satış';
          const tutarLabel = tur === 'Alış' ? 'Alış' : tur === 'İade' ? 'İade' : 'Satış';
          const maliyetLabel = row.fin.live ? 'Maliyet (anlık)' : 'Maliyet';
          return (
            <Box key={row.islem.id} sx={{ py: 0.55, borderBottom: `1px solid ${fx.border}`, flexShrink: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{row.islem.urun || row.islem.islemNo || '—'}</Typography>
              <Typography sx={{ fontSize: 11, color: fx.muted }}>{ts} · {tur} · {row.islem.islemNo || ''}</Typography>
              <Typography sx={{ fontSize: 12, mt: 0.25 }}>
                {tutarLabel} {formatTry((row.fin.saleAmountTry != null ? row.fin.saleAmountTry : row.islem.toplamTutar) * row.sign)}
                {' · '}{maliyetLabel} {row.fin.ok ? formatTry(row.fin.totalCostTry * row.sign) : 'eksik'}
                {' · '}Kâr {row.fin.ok ? formatTry(row.fin.grossProfitTry * row.sign) : '—'}
                {row.fin.ok ? ` · ${formatPct(row.fin.grossMarginPct)}` : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {list.length > PAGE_SIZE ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pt: 1.1, flexShrink: 0, borderTop: `1px solid ${fx.border}` }}>
          <Button size="small" disabled={safePage <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))} sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>
            ‹
          </Button>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.35 }}>
            {Array.from({ length: pages }, (_, i) => (
              <Button
                key={i}
                size="small"
                onClick={() => setPage(i)}
                sx={{
                  minWidth: 28,
                  height: 28,
                  p: 0,
                  fontSize: 12.5,
                  fontWeight: 800,
                  textTransform: 'none',
                  color: i === safePage ? '#fff' : fx.text,
                  bgcolor: i === safePage ? '#1d4ed8' : 'transparent',
                  '&:hover': { bgcolor: i === safePage ? '#1d4ed8' : fx.primarySoft }
                }}
              >
                {i + 1}
              </Button>
            ))}
          </Box>
          <Button size="small" disabled={safePage >= pages - 1} onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>
            ›
          </Button>
        </Box>
      ) : null}
    </Drawer>
  );
}
